import { CollectRouteMapProps } from '@/utils/type';
import { isEmpty } from '@/utils/util';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CollectMapApi from '@/fetch/bussiness/collectMap';
import { HttpStatusCode } from '@/fetch/core/constant';
import './index.scss';
import StandardTileMap from '@/components/StandardTileMap';
import MapOperations from '@/utils/MapOperations';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';

const fetchApi = new CollectMapApi();

const CollectRouteMap = (props: CollectRouteMapProps) => {
  const {
    latitude,
    longitude,
    taskRouteList,
    fourLaneIndexList,
    rotation,
    centerMark,
  } = props;
  const { controlBtn } = useSelector((state: RootState) => state);
  const [specialMarkList, setSpecialMarkList] = useState<any[]>([]);
  const [isLocalView, setIsLocalView] = useState<boolean>(false);
  const mapRef = useRef<any>(null); // 地图实例
  const mapOperateRef = useRef<MapOperations | null>(null); // 地图操作方法的类的实例
  const mapContainerRef = useRef<HTMLDivElement>(null); // 地图容器DOM元素
  const dragEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const mouseDownRef = useRef<boolean>(false); // 鼠标是否按下
  const mouseMoveRef = useRef<boolean>(false); // 鼠标是否移动
  const isMountedRef = useRef(true);
  const isMapInitialized = useMemo(
    () => mapRef.current !== null,
    [mapRef.current]
  );
  /**
   * 获取附近的特殊标记
   */
  const getSpecialMarkers = async (latitude: number, longitude: number) => {
    try {
      const res = await fetchApi.getNearbyMarks({
        latitude,
        longitude,
      });
      if (!isMountedRef.current) return;
      if (res.code === HttpStatusCode.Success) {
        if (res.data.markList && !isEmpty(res.data.markList)) {
          setSpecialMarkList(res.data.markList);
        } else {
          setSpecialMarkList([]);
        }
      }
    } catch (e) {
      console.error('Error fetching special markers:', e);
    }
  };

  /**
   * 绘制中心点（车辆位置）
   */
  const drawCenterMark = () => {
    if (!latitude || !longitude || !mapOperateRef.current) {
      return;
    }
    // OpenLayers使用[longitude, latitude]的顺序
    mapOperateRef.current.updateRealTimePosition(
      [longitude, latitude],
      rotation,
      false
    );
    !isDraggingRef.current &&
      mapOperateRef.current.changeCenter([longitude, latitude]);

    // 获取附近的特殊标记
    getSpecialMarkers(latitude, longitude);
  };

  /**
   * 绘制采图路线
   */
  const drawTaskRoutePath = () => {
    if (!taskRouteList || isEmpty(taskRouteList) || !mapOperateRef.current) {
      return;
    }

    mapOperateRef.current.drawTaskRoutePath(taskRouteList);
  };

  /**
   * 绘制四车道
   */
  const drawFourLanePath = () => {
    if (
      !taskRouteList ||
      !fourLaneIndexList ||
      isEmpty(fourLaneIndexList) ||
      !mapOperateRef.current
    ) {
      return;
    }

    mapOperateRef.current.drawFourLanePath(taskRouteList, fourLaneIndexList);
  };

  /**
   * 绘制起点和终点
   */
  const drawStartAndEnd = () => {
    if (!taskRouteList || isEmpty(taskRouteList) || !mapOperateRef.current) {
      return;
    }

    mapOperateRef.current.drawStartAndEnd(taskRouteList);
  };

  /**
   * 绘制特殊标记
   */
  const drawSpecialMarker = () => {
    if (
      !specialMarkList ||
      isEmpty(specialMarkList) ||
      !mapOperateRef.current
    ) {
      return;
    }
    mapOperateRef.current.drawSpecialMarkers(specialMarkList);
  };

  /**
   * 地图拖动结束事件处理
   */
  const handleMapMoveEnd = () => {
    if (!mapRef.current || !mapOperateRef.current) {
      return;
    }

    // 获取地图中心点
    const center = mapRef.current.getView().getCenter();
    if (center && center.length === 2) {
      getSpecialMarkers(center[1], center[0]); // OpenLayers中心点是[longitude, latitude]
    }
  };

  /**
   * 初始化地图操作实例
   */
  const initMapOperate = (map: any) => {
    mapRef.current = map;

    if (mapRef.current) {
      mapOperateRef.current = new MapOperations(mapRef.current);

      // 添加地图移动结束事件监听
      mapRef.current.on('moveend', handleMapMoveEnd);

      // 初始绘制
      if (latitude && longitude) {
        drawCenterMark();
      }

      if (taskRouteList && !isEmpty(taskRouteList)) {
        drawTaskRoutePath();
        drawStartAndEnd();
      }

      if (fourLaneIndexList && !isEmpty(fourLaneIndexList)) {
        drawFourLanePath();
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (mapRef.current) {
        mapRef.current.un('moveend', handleMapMoveEnd);
        mapRef.current.setTarget(null);
        mapRef.current = null;
      }
      if (mapOperateRef.current) {
        mapOperateRef.current = null;
      }
    };
  }, []);

  // 监听位置变化
  useEffect(() => {
    if (latitude && longitude) {
      drawCenterMark();
    } else {
      if (centerMark) {
        mapOperateRef.current?.changeCenter([
          centerMark.longitude,
          centerMark.latitude,
        ]);
      }
    }
  }, [latitude, longitude, centerMark, isDraggingRef.current]);

  // 监听任务路线变化
  useEffect(() => {
    if (taskRouteList && !isEmpty(taskRouteList)) {
      drawTaskRoutePath();
      drawStartAndEnd();
    }
  }, [taskRouteList]);

  // 监听四车道变化
  useEffect(() => {
    if (fourLaneIndexList && !isEmpty(fourLaneIndexList)) {
      drawFourLanePath();
    }
  }, [fourLaneIndexList]);

  // 监听特殊标记变化
  useEffect(() => {
    if (specialMarkList && !isEmpty(specialMarkList)) {
      drawSpecialMarker();
    }
  }, [specialMarkList]);

  useEffect(() => {
    controlBtn.LC_BUTTON_PLUS && handleToggleView();
  }, [controlBtn.LC_BUTTON_PLUS]);

  useEffect(() => {
    // 监听地图拖拽事件
    if (!isMapInitialized) {
      return;
    }
    if (mapContainerRef.current) {
      const mapContainer = mapContainerRef.current;
      mapContainer.addEventListener('mousedown', handleMouseDown);
      mapContainer.addEventListener('mousemove', handleMouseMove);
      mapContainer.addEventListener('mouseup', handleMouseUp);
      mapContainer.addEventListener('mouseleave', handleMouseUp);
    }

    return () => {
      if (mapContainerRef.current) {
        const mapContainer = mapContainerRef.current;
        mapContainer.removeEventListener('mousedown', handleMouseDown);
        mapContainer.removeEventListener('mousemove', handleMouseMove);
        mapContainer.removeEventListener('mouseup', handleMouseUp);
        mapContainer.removeEventListener('mouseleave', handleMouseUp);
      }
      if (dragEndTimerRef.current) {
        clearTimeout(dragEndTimerRef.current);
        dragEndTimerRef.current = null;
      }
    };
  }, [isMapInitialized]);
  const handleMouseDown = () => {
    mouseDownRef.current = true;
    mouseMoveRef.current = false;
  };
  const handleMouseMove = () => {
    if (mouseDownRef.current) {
      mouseMoveRef.current = true;
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        if (dragEndTimerRef.current) {
          clearTimeout(dragEndTimerRef.current);
          dragEndTimerRef.current = null;
        }
      }
    }
  };
  const handleMouseUp = () => {
    if (mouseDownRef.current && mouseMoveRef.current && isDraggingRef.current) {
      if (dragEndTimerRef.current) {
        clearTimeout(dragEndTimerRef.current);
      }
      dragEndTimerRef.current = setTimeout(() => {
        isDraggingRef.current = false;
        if (mapOperateRef.current && longitude && latitude) {
          mapOperateRef.current.changeCenter([longitude, latitude]);
        }
        dragEndTimerRef.current = null;
      }, 5000);
    }
    mouseDownRef.current = false;
    mouseMoveRef.current = false;
  };

  const handleToggleView = () => {
    if (!mapOperateRef.current) {
      return;
    }
    const newIsLocal = !isLocalView;
    setIsLocalView(newIsLocal);
    if (newIsLocal) {
      mapOperateRef.current.setZoom(22);
    } else {
      mapOperateRef.current.setZoom(15);
    }
    drawCenterMark();
  };

  return (
    <div className="collect-route-map-container" ref={mapContainerRef}>
      <div className="toggle-local-all-btn" onClick={handleToggleView}>
        <span>{isLocalView ? '切换总览' : '切换局部'}</span>
      </div>
      <StandardTileMap
        latitude={latitude || centerMark?.latitude || 34.53506}
        longitude={longitude || centerMark?.longitude || 108.93885}
        zoom={15}
        forceAllHighPrecisionMapVisible={isLocalView}
        getMapRef={initMapOperate}
      />
    </div>
  );
};

export default React.memo(CollectRouteMap);
